import type { PassThroughOptionType } from '../utils';

/** What a step's indicator and screen-reader text communicate. */
export enum StepperStepState {
  INCOMPLETE = 'incomplete',
  COMPLETE = 'complete',
  INVALID = 'invalid',
  LOCKED = 'locked',
  UNAVAILABLE = 'unavailable',
}

export type MeridiaStepperStep = {
  value: number;
  label: string;
  /** Material Symbols Outlined ligature name, e.g. 'upload_file'. */
  icon: string;
  state: `${StepperStepState}`;
  /** Not navigable. Rendered with `aria-disabled`, never the `disabled` attribute. */
  disabled?: boolean;
  /** Visible line under the title, e.g. why the step is unavailable. */
  caption?: string;
};

/** Screen-reader text per state, already translated. */
export type MeridiaStepperStateLabels = Record<`${StepperStepState}`, string>;

export type MeridiaStepperStepContext = {
  step: MeridiaStepperStep;
  index: number;
  active: boolean;
};

export type MeridiaStepperPassThrough = {
  /** The `<nav>`. */
  root?: PassThroughOptionType;
  /** The `<ol>`. */
  list?: PassThroughOptionType;
  /** Each step `<button>`; a function receives `{ context: { step, index, active } }`. */
  step?: PassThroughOptionType<
    Record<string, unknown>,
    { context: MeridiaStepperStepContext }
  >;
  /** Each caption element. */
  caption?: PassThroughOptionType;
};

export interface MeridiaStepperProps {
  /** `value` of the active step. */
  value: number;
  /** Steps in display order; each `value` must be unique. */
  steps: MeridiaStepperStep[];
  /** Screen-reader text per state, already translated. */
  stateLabels: MeridiaStepperStateLabels;
  /** Accessible name of the `<nav>`. */
  ariaLabel: string;
  /** `root` nav, `list` ol, `step` button (may be a function of `{ context }`), `caption`. */
  pt?: MeridiaStepperPassThrough;
}
